// =====================================================
// ACTIVIDAD AGRÍCOLA - TAB DEL PRODUCTOR
// src/pages/Agro/Productor/ActividadAgricola.js
// =====================================================
// MODIFICACIONES:
// - Se cargan valores de referencia de valor_agro (getValoresAgro)
// - Resumen por Cultivo ahora incluye:
//   * Ingreso Bruto (precio × producción)
//   * Costo Total (suma de costos × superficie)
//   * Resultado Neto (ingreso - costos)
//   * Los valores son de referencia pero editables por el cliente
// - Las siembras se cruzan con la zona del establecimiento
// =====================================================

import React, { useState, useEffect, useMemo } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap"
import DataTable from "react-data-table-component"
import { opcionesPaginacion } from "components/Tables/paginacion"
import classnames from "classnames"
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts"
import {
  formatearNumero,
  formatearMonto,
  formatearFecha,
  formatearFechaSiembra,
  redondearArriba,
} from "helpers/formatters"

import {
  getSiembrasPorProductor,
  getSiembra,
  deleteSiembra,
  deleteStockAgricola,
  getResumenCultivos,
  getEstablecimientos,
  getCampanas,
  getValoresAgro,
  getStockAgricolaPorProductor,
  getResumenAgricola,
  getTodosMovimientosAgricola,
} from "api/agro"
import toastHandler from "toastHandler"
import { FormSiembra } from "../Siembra/FormSiembra"
import FormStockAgricola from "../StockAgricola/FormStockAgricola"
import FormMovimientoAgricola from "../StockAgricola/FormMovimientoAgricola"
import useAuthorization from "hooks/useAuthorization"
import { TEXTO_SIEMBRAS_VIGENTES } from "./SiembrasVigentesNota"

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
]

const formatRedondeadoArriba = value => formatearNumero(redondearArriba(value))

// BDSJEA-487: Stock Actual muestra Cantidad (QQ) y Valuación (USD) como enteros con
// el redondeo normal de formatearNumero (NO el redondeo hacia arriba de los totales de
// importes de BDSJEA-474: son cantidades físicas). Sin dato = "-"; sin "-0".
const formatEnteroStock = value =>
  value == null || isNaN(value)
    ? "-"
    : formatearNumero(Math.round(Number(value)) + 0, 0)

// BDSJEA-202: oculto hasta implementar lógica stock = siembra + movimientos ingreso/egreso
const MOSTRAR_TAB_MOVIMIENTOS = false

const TEXTO_TOTALES_PARCIALES =
  "Totales parciales: hay siembras sin valores de referencia en su zona"

const ActividadAgricola = ({ idProductor }) => {
  const { puedeModificarObjeto } = useAuthorization()
  const [loading, setLoading] = useState(true)
  const [siembras, setSiembras] = useState([])
  const [resumen, setResumen] = useState([])
  const [establecimientos, setEstablecimientos] = useState([])
  const [campanas, setCampanas] = useState([])
  const [valoresAgro, setValoresAgro] = useState([])
  const [filtros, setFiltros] = useState({
    establecimiento: "",
    campana: "",
    cultivo: "",
  })
  const [modalNueva, setModalNueva] = useState(false)

  // Estado para overrides del cliente (edición de valores)
  const [overrides, setOverrides] = useState({})

  // ===== STOCK AGRÍCOLA =====
  const [stockAgricola, setStockAgricola] = useState([])
  const [resumenAgricola, setResumenAgricola] = useState(null)
  const [movimientosAgricola, setMovimientosAgricola] = useState([])
  const [activeTabAgricola, setActiveTabAgricola] = useState("siembras")
  const [modalStockAgricola, setModalStockAgricola] = useState(false)
  const [modalMovimientoAgricola, setModalMovimientoAgricola] = useState(false)

  // ===== EDITAR / ELIMINAR =====
  const [siembraAEditar, setSiembraAEditar] = useState(null)
  const [modalEditarSiembra, setModalEditarSiembra] = useState(false)
  const [siembraAEliminar, setSiembraAEliminar] = useState(null)
  const [stockAEditar, setStockAEditar] = useState(null)
  const [modalEditarStock, setModalEditarStock] = useState(false)
  const [stockAEliminar, setStockAEliminar] = useState(null)
  const setSuccess = toastHandler(state => state.setSuccess)
  const setError = toastHandler(state => state.setError)

  useEffect(() => {
    loadData()
  }, [idProductor])

  const loadData = async () => {
    setLoading(true)
    try {
      const [
        siembrasData,
        resumenData,
        estData,
        campData,
        vaData,
        stockData,
        resumenAgroData,
      ] = await Promise.all([
        getSiembrasPorProductor(idProductor),
        getResumenCultivos(idProductor),
        getEstablecimientos(idProductor),
        getCampanas(),
        getValoresAgro().catch(() => []),
        getStockAgricolaPorProductor(idProductor).catch(() => []),
        getResumenAgricola(idProductor).catch(() => null),
      ])
      setSiembras(siembrasData)
      setResumen(resumenData)
      setEstablecimientos(estData)
      setCampanas(campData)
      setValoresAgro(vaData)
      setStockAgricola(stockData)
      setResumenAgricola(resumenAgroData)
    } catch (error) {
      console.error("Error al cargar datos:", error)
    } finally {
      setLoading(false)
    }
  }

  // BDSJEA-208 hotfix: refresco liviano tras borrar una siembra. Actualiza
  // solo lo que depende del listado de siembras (grafico de torta y tabla
  // financiera de "Resumen por Cultivo", resumen de "Stock Actual"), sin
  // volver a pedir las otras 5 fuentes de loadData (establecimientos,
  // campañas, valores agro, stock agrícola, y la propia lista de siembras,
  // que ya se actualiza en el estado local) ni disparar el loading global de
  // toda la pantalla. Si falla, no rompe el flujo de borrado (que ya mostró
  // el toast de éxito): el resumen queda con el dato anterior y se corrige
  // en la próxima carga completa de la pantalla.
  const refrescarResumenSiembras = async () => {
    try {
      const [resumenData, resumenAgroData] = await Promise.all([
        getResumenCultivos(idProductor),
        getResumenAgricola(idProductor).catch(() => null),
      ])
      setResumen(resumenData)
      setResumenAgricola(resumenAgroData)
    } catch (error) {
      console.error(
        "Error al refrescar el resumen tras eliminar siembra:",
        error,
      )
    }
  }

  // Cargar movimientos agrícolas
  const loadMovimientosAgricola = async () => {
    try {
      setMovimientosAgricola([])
      const mov = await getTodosMovimientosAgricola(null, null, idProductor)
      setMovimientosAgricola(mov)
    } catch (error) {
      console.error("Error cargando movimientos agrícolas:", error)
      setMovimientosAgricola([])
    }
  }

  useEffect(() => {
    if (activeTabAgricola === "movimientos") {
      loadMovimientosAgricola()
    }
  }, [activeTabAgricola])

  const movimientosAgricolaFiltrados = useMemo(() => {
    return movimientosAgricola.filter(m => {
      if (
        filtros.establecimiento &&
        Number(m.idEstablecimiento) !== Number(filtros.establecimiento)
      )
        return false
      if (filtros.cultivo && m.cultivo !== filtros.cultivo) return false
      return true
    })
  }, [movimientosAgricola, filtros.establecimiento, filtros.cultivo])

  // Filtrar siembras
  const siembrasFiltradas = siembras.filter(s => {
    if (
      filtros.establecimiento &&
      s.idEstablecimiento !== parseInt(filtros.establecimiento)
    )
      return false
    if (filtros.campana && s.campana !== filtros.campana) return false
    if (filtros.cultivo && s.cultivo !== filtros.cultivo) return false
    return true
  })

  // Filtrar stock agrícola
  const stockAgricolaFiltrado = stockAgricola.filter(s => {
    if (
      filtros.establecimiento &&
      Number(s.idEstablecimiento) !== Number(filtros.establecimiento)
    )
      return false
    if (filtros.campana && s.campania !== filtros.campana) return false
    if (filtros.cultivo && s.cultivo !== filtros.cultivo) return false
    return true
  })

  // BDSJEA-502: criterio de vigencia compartido con el Informe Agropecuario y la
  // Proyección. El backend marca `vigente` en cada siembra; si el campo todavía
  // no existe (backend viejo) se asume vigente. El listado de la pestaña
  // "Siembras" sigue mostrando todo el historial: solo el cálculo se acota.
  const siembrasVigentes = useMemo(
    () => siembras.filter(s => s.vigente !== false),
    [siembras],
  )

  const campanasIncluidas = useMemo(
    () =>
      [...new Set(siembrasVigentes.map(s => s.campana).filter(Boolean))].sort(),
    [siembrasVigentes],
  )

  // =====================================================
  // BDSJEA-502: resumen por cultivo calculado POR SIEMBRA en el navegador, con la
  // zona del establecimiento de cada una (idEstablecimiento -> nombreZona), y
  // agrupado por cultivo. El /resumen del backend viene agregado por cultivo y
  // pierde la zona: solo se usa para conservar el orden (y la numeración
  // `cultivo_${idx}` de los overrides).
  // =====================================================
  const resumenCultivos = useMemo(() => {
    const porCultivo = new Map()
    siembrasVigentes.forEach(s => {
      const nombre = s.cultivo || ""
      if (!porCultivo.has(nombre)) porCultivo.set(nombre, [])
      porCultivo.get(nombre).push(s)
    })
    const ordenBase = resumen.map(r => r.nombreCultivo || r.cultivo || "")
    const extras = [...porCultivo.keys()].filter(n => !ordenBase.includes(n))
    // idx = posición en el orden original (resumen del backend + extras): no
    // cambia aunque un cultivo quede sin siembras vigentes.
    return [...ordenBase, ...extras]
      .map((nombre, idx) => ({ nombre, idx, lista: porCultivo.get(nombre) }))
      .filter(x => x.lista && x.lista.length > 0)
      .map(({ nombre, idx, lista }) => ({
        idx,
        nombreCultivo: nombre,
        codigoCultivo: lista[0].codigoCultivo || "",
        siembras: lista,
        cantidadSiembras: lista.length,
        superficieTotalHa: lista.reduce(
          (acc, s) => acc + (parseFloat(s.superficieHa) || 0),
          0,
        ),
      }))
  }, [siembrasVigentes, resumen])

  // Datos para el gráfico
  const chartData = resumenCultivos.map(r => ({
    name: r.nombreCultivo,
    value: parseFloat(r.superficieTotalHa) || 0,
  }))

  // =====================================================
  // CÁLCULO DE RESUMEN FINANCIERO POR CULTIVO
  // Cruza cada siembra con valor_agro de SU zona (BDSJEA-502: D3)
  // =====================================================
  const resumenFinanciero = useMemo(() => {
    const stockPorCultivo = resumenAgricola?.detallePorCultivo || {}
    const hayStock = Object.keys(stockPorCultivo).length > 0

    if (!resumenCultivos.length && !hayStock) return []

    // Zona de cada establecimiento (EstablecimientoListDTO.nombreZona)
    const estMap = {}
    establecimientos.forEach(e => {
      estMap[e.id] = e
    })

    // Mapa de valores agro por zona+cultivo
    const vaMap = {}
    valoresAgro.forEach(va => {
      const zonaKey = (va.zona || va.nombreZona || "").toLowerCase()
      const cultivoKey = (va.cultivo || va.nombreCultivo || "").toLowerCase()
      if (zonaKey && cultivoKey) {
        vaMap[`${zonaKey}|${cultivoKey}`] = va
      }
    })

    // Valor agro de la zona + cultivo. Sin fila de ESA zona => null ("s/d"):
    // nunca se cae a otra zona (mismo criterio que BDSJEA-125 y 144).
    const buscarValorAgro = (zona, nombreCultivo) => {
      const zonaKey = (zona || "").toLowerCase()
      const cultivoLower = (nombreCultivo || "").toLowerCase()
      if (!zonaKey || !cultivoLower) return null
      const exacto = vaMap[`${zonaKey}|${cultivoLower}`]
      if (exacto) return exacto
      for (const va of valoresAgro) {
        const zonaVa = (va.zona || va.nombreZona || "").toLowerCase()
        if (zonaVa !== zonaKey) continue
        const cultivoVa = (va.cultivo || va.nombreCultivo || "").toLowerCase()
        if (
          cultivoVa &&
          (cultivoVa.includes(cultivoLower) || cultivoLower.includes(cultivoVa))
        ) {
          return va
        }
      }
      return null
    }

    const num = v => parseFloat(v) || 0

    // Filas de siembras (enriquecidas con stock si existe)
    const siembraRows = resumenCultivos.map(r => {
      const key = `cultivo_${r.idx}`
      const superficieHa = parseFloat(r.superficieTotalHa) || 0

      let produccionTn = 0
      let ingresoBruto = 0
      let costoTotal = 0
      let sinValores = 0
      let sinZona = 0
      let supConRendRef = 0
      let rendRefPonderado = 0
      const rendRefDistintos = new Set()
      let precioRef = 0
      let costosDirectos = 0
      let gastosComercializacion = 0
      let arrendamientos = 0
      let adminSiembra = 0
      let seguroGranizo = 0

      r.siembras.forEach(s => {
        const sup = num(s.superficieHa)
        const zona = estMap[s.idEstablecimiento]?.nombreZona
        const va = buscarValorAgro(zona, r.nombreCultivo)
        if (!zona) sinZona += 1
        // Producción: SIEMPRE el rendimiento esperado propio de la siembra si es
        // > 0 (tenga o no fila de valor_agro en su zona); si es 0 cae al de
        // valor_agro de su zona; sin ninguno la siembra no suma producción.
        const rendVa = va ? num(va.rendimiento) : 0
        const rendSiembra = num(s.rendimientoEsperadoQqHa)
        const prod = (sup * (rendSiembra > 0 ? rendSiembra : rendVa)) / 10 // qq/ha a tn
        produccionTn += prod
        // Ingreso y costo: sin fila de ESA zona => "s/d" (BDSJEA-144).
        if (!va) {
          sinValores += 1
          return
        }
        const precio = num(va.precio)
        // costos_total ya contiene las 5 partidas (V0128): no se vuelven a sumar.
        const costoHa = num(va.costosTotal || va.costos_total)
        ingresoBruto += prod * precio
        costoTotal += sup * costoHa
        // Desglose informativo (por superficie): NO se suma al costo.
        if (rendVa > 0) {
          rendRefDistintos.add(rendVa)
          supConRendRef += sup
          rendRefPonderado += sup * rendVa
        }
        precioRef = precio
        costosDirectos += sup * num(va.costoDirecto || va.costo_directo)
        gastosComercializacion +=
          sup * num(va.gastosComercializacion || va.gastos_comercializacion)
        arrendamientos += sup * num(va.arrendamientos)
        adminSiembra +=
          sup * num(va.administracionSiembra || va.administracion_siembra)
        seguroGranizo += sup * num(va.seguroGranizo || va.seguro_granizo)
      })

      const sinDatos = sinValores === r.siembras.length
      // Rendimiento de referencia: ponderado por superficie de las siembras que
      // tienen fila en su zona; si hay más de un valor distinto no se muestra (BDSJEA-512).
      const rendimientoRef =
        supConRendRef > 0 && rendRefDistintos.size <= 1
          ? rendRefPonderado / supConRendRef
          : 0

      // Aplicar overrides del cliente si existen
      const ov = overrides[key] || {}
      if (ov.ingresoBruto != null) ingresoBruto = parseFloat(ov.ingresoBruto)
      if (ov.costoTotal != null) costoTotal = parseFloat(ov.costoTotal)
      const resultadoNeto = ingresoBruto - costoTotal

      // Stock registrado para este cultivo (BDSJEA-190)
      const stockQq = parseFloat(stockPorCultivo[r.nombreCultivo] || 0)

      return {
        nombreCultivo: r.nombreCultivo,
        codigoCultivo: r.codigoCultivo,
        cantidadSiembras: r.cantidadSiembras,
        key,
        superficieHa,
        produccionTn,
        rendimientoRef,
        precioRef,
        costosDirectos,
        gastosComercializacion,
        arrendamientos,
        adminSiembra,
        seguroGranizo,
        ingresoBruto,
        costoTotal,
        resultadoNeto,
        tieneValoresRef: sinValores === 0,
        parcial: sinValores > 0 && !sinDatos,
        sinZona,
        sinDatos,
        stockQq,
      }
    })

    // Filas extra: cultivos con stock pero sin siembra (BDSJEA-190)
    const cultivosEnSiembras = new Set(
      resumenCultivos.map(r => r.nombreCultivo),
    )
    const stockOnlyRows = Object.entries(stockPorCultivo)
      .filter(([nombre]) => !cultivosEnSiembras.has(nombre))
      .map(([nombre, qqRaw], idx) => {
        const stockQq = parseFloat(qqRaw) || 0
        return {
          nombreCultivo: nombre,
          codigoCultivo: "",
          key: `stock_${idx}`,
          superficieHa: 0,
          produccionTn: 0,
          cantidadSiembras: 0,
          stockQq,
          rendimientoRef: 0,
          precioRef: 0,
          costosDirectos: 0,
          gastosComercializacion: 0,
          arrendamientos: 0,
          adminSiembra: 0,
          seguroGranizo: 0,
          ingresoBruto: 0,
          costoTotal: 0,
          resultadoNeto: 0,
          tieneValoresRef: true,
          sinDatos: false,
        }
      })

    return [...siembraRows, ...stockOnlyRows]
  }, [
    resumenCultivos,
    valoresAgro,
    establecimientos,
    overrides,
    resumenAgricola,
  ])

  // Totales
  const totales = useMemo(() => {
    return resumenFinanciero.reduce(
      (acc, r) => ({
        superficie: acc.superficie + r.superficieHa,
        siembras: acc.siembras + (r.cantidadSiembras || 0),
        stockQq: acc.stockQq + (r.stockQq || 0),
        ingresoBruto: acc.ingresoBruto + r.ingresoBruto,
        costoTotal: acc.costoTotal + r.costoTotal,
        resultadoNeto: acc.resultadoNeto + r.resultadoNeto,
      }),
      {
        superficie: 0,
        siembras: 0,
        stockQq: 0,
        ingresoBruto: 0,
        costoTotal: 0,
        resultadoNeto: 0,
      },
    )
  }, [resumenFinanciero])

  // Columnas de la tabla de siembras
  const columns = [
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
      width: "140px",
      cell: row => (
        <div>
          <strong>{row.cultivo}</strong>
          {row.variedad && (
            <small className="d-block text-muted">{row.variedad}</small>
          )}
        </div>
      ),
    },
    {
      name: "Establecimiento",
      selector: row => row.establecimiento,
      sortable: true,
      grow: 1,
      minWidth: "130px",
    },
    {
      name: "Superficie",
      selector: row => row.superficieHa,
      sortable: true,
      right: true,
      width: "110px",
      format: row => `${row.superficieHa?.toLocaleString("es-AR")} ha`,
    },
    {
      name: "Fecha Siembra",
      // BDSJEA-512: las siembras importadas guardan solo la fecha del plan (fechaSiembraEstimada).
      selector: row => row.fechaSiembra ?? row.fechaSiembraEstimada ?? "",
      sortable: true,
      width: "180px",
      // BDSJEA-539: sin la marca «(est.)» en esta columna
      format: row =>
        formatearFechaSiembra(
          row.fechaSiembra,
          row.fechaSiembraEstimada,
          "-",
          false,
        ),
    },
    {
      // BDSJEA-515: nivel de rendimiento PROPIO de la siembra (no el del establecimiento); «-» si no tiene o si el backend es anterior.
      name: "Rendimiento",
      selector: row => row.nivelRendimientoDescripcion ?? "",
      sortable: true,
      width: "150px",
      cell: row => row.nivelRendimientoDescripcion || "-",
    },
    {
      name: "Estado",
      selector: row => row.estadoFenologico,
      width: "130px",
      cell: row => (
        <Badge color="info" className="px-2">
          {row.estadoFenologico || "Sin datos"}
        </Badge>
      ),
    },
    {
      name: "Campaña",
      selector: row => row.campana,
      width: "100px",
    },
    {
      name: "Cosechado",
      selector: row => row.fechaCosecha,
      width: "100px",
      center: true,
      cell: row =>
        row.fechaCosecha ? (
          <Badge color="success">
            <i className="mdi mdi-check" /> Sí
          </Badge>
        ) : (
          <Badge color="secondary">No</Badge>
        ),
    },
    {
      name: "",
      width: "80px",
      center: true,
      cell: row =>
        puedeModificarObjeto("/agro/establecimientos") ? (
          <div className="d-flex gap-1">
            <Button
              color="link"
              className="p-0 text-primary"
              title="Editar"
              onClick={async () => {
                const detalle = await getSiembra(row.id)
                setSiembraAEditar(detalle)
                setModalEditarSiembra(true)
              }}
            >
              <i className="mdi mdi-pencil" />
            </Button>
            <Button
              color="link"
              className="p-0 text-danger"
              title="Eliminar"
              onClick={() => setSiembraAEliminar(row)}
            >
              <i className="mdi mdi-trash-can" />
            </Button>
          </div>
        ) : null,
    },
  ]

  // =====================================================
  // STOCK AGRÍCOLA - COLUMNAS
  // =====================================================
  const formatNumero = (val, decimals = 0) => {
    if (val == null || isNaN(val)) return "-"
    return Number(val).toLocaleString("es-AR", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  }

  const columnsStockAgricola = [
    {
      name: "Establecimiento",
      selector: row => row.nombreEstablecimiento,
      sortable: true,
      grow: 2,
      minWidth: "200px",
    },
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
      grow: 0,
      minWidth: "120px",
    },
    {
      name: "Almacenamiento",
      selector: row => row.tipoAlmacenamiento,
      sortable: true,
      grow: 0,
      minWidth: "120px",
      format: row => row.tipoAlmacenamiento || "-",
    },
    {
      name: (
        <>
          Cantidad
          <br />
          (QQ)
        </>
      ),
      selector: row => row.cantidadQq,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "110px",
      format: row => formatEnteroStock(row.cantidadQq),
    },
    {
      name: (
        <>
          Valuación
          <br />
          (USD)
        </>
      ),
      selector: row => row.valuacionTotalUsd,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "130px",
      format: row =>
        row.valuacionTotalUsd && row.valuacionTotalUsd > 0
          ? formatEnteroStock(row.valuacionTotalUsd)
          : "-",
    },
    {
      name: (
        <>
          Fecha
          <br />
          Stock
        </>
      ),
      selector: row => row.fechaStock,
      sortable: true,
      grow: 0,
      minWidth: "130px",
      format: row => formatearFecha(row.fechaStock),
    },
    {
      name: "Lote",
      selector: row => row.lote,
      grow: 0,
      minWidth: "90px",
      format: row => row.lote || "-",
    },
    {
      name: "Campaña",
      selector: row => row.campania,
      grow: 0,
      minWidth: "90px",
      format: row => row.campania || "-",
    },
    {
      name: "",
      width: "80px",
      center: true,
      cell: row =>
        puedeModificarObjeto("/agro/establecimientos") ? (
          <div className="d-flex gap-1">
            <Button
              color="link"
              className="p-0 text-primary"
              title="Editar"
              onClick={() => {
                setStockAEditar(row)
                setModalEditarStock(true)
              }}
            >
              <i className="mdi mdi-pencil" />
            </Button>
            <Button
              color="link"
              className="p-0 text-danger"
              title="Eliminar"
              onClick={() => setStockAEliminar(row)}
            >
              <i className="mdi mdi-trash-can" />
            </Button>
          </div>
        ) : null,
    },
  ]

  const columnsMovimientosAgricola = [
    {
      name: "Fecha",
      selector: row => row.fechaMovimiento,
      sortable: true,
      grow: 0,
      minWidth: "110px",
      format: row => formatearFecha(row.fechaMovimiento),
    },
    {
      name: "Establecimiento",
      selector: row => row.nombreEstablecimiento,
      sortable: true,
      grow: 3,
      minWidth: "220px",
    },
    {
      name: "Tipo",
      selector: row => row.tipoMovimiento,
      sortable: true,
      grow: 0,
      minWidth: "90px",
      cell: row => (
        <Badge color={row.tipoMovimiento === "INGRESO" ? "success" : "danger"}>
          {row.tipoMovimiento}
        </Badge>
      ),
    },
    {
      name: "Motivo",
      selector: row => row.motivo,
      sortable: true,
      grow: 0,
      minWidth: "110px",
    },
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
      grow: 0,
      minWidth: "120px",
    },
    {
      name: "Almacenamiento",
      selector: row => row.tipoAlmacenamiento,
      grow: 0,
      minWidth: "110px",
      format: row => row.tipoAlmacenamiento || "-",
    },
    {
      name: (
        <>
          Cantidad
          <br />
          (QQ)
        </>
      ),
      selector: row => row.cantidadQq,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "100px",
      format: row => formatNumero(row.cantidadQq, 2),
    },
    {
      name: (
        <>
          Importe
          <br />
          (USD/ARS)
        </>
      ),
      selector: row => row.importeTotal,
      sortable: true,
      right: true,
      grow: 0,
      minWidth: "110px",
      format: row =>
        row.importeTotal ? formatNumero(row.importeTotal, 2) : "-",
    },
    {
      name: "DTE/Guía",
      selector: row => row.numeroDte,
      grow: 0,
      minWidth: "100px",
      format: row => row.numeroDte || "-",
    },
  ]

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        <p className="mt-3 text-muted">Cargando actividad agrícola...</p>
      </div>
    )
  }

  return (
    <>
      {/* KPIs Stock Agrícola - arriba de todo */}
      {(stockAgricola.length > 0 || resumenAgricola) && (
        <Row className="mb-3">
          <Col md={6}>
            <Card className="bg-success text-white">
              <CardBody className="text-center">
                <h3 className="mb-0" data-testid="kpi-stock-valuacion">
                  {formatearMonto(
                    resumenAgricola?.valuacionTotalUsd ?? 0,
                    "USD",
                    0,
                  )}
                </h3>
                <small>
                  Stock: Valuación total
                  <i
                    className="mdi mdi-information-outline ms-1 text-white-50"
                    style={{ fontSize: 12, cursor: "help" }}
                    title="Suma por registro de (cantidad QQ × 100 kg × precio de referencia USD/kg) del stock agrícola"
                  />
                </small>
              </CardBody>
            </Card>
          </Col>
          <Col md={6}>
            <Card
              className={`text-white ${totales.resultadoNeto < 0 ? "bg-danger" : "bg-info"}`}
            >
              <CardBody className="text-center">
                <h3 className="mb-0" data-testid="kpi-resultado-neto">
                  {formatearMonto(
                    redondearArriba(totales.resultadoNeto),
                    "USD",
                    0,
                  )}
                </h3>
                <small>
                  Siembras: Resultado Neto
                  <i
                    className="mdi mdi-information-outline ms-1 text-white-50"
                    style={{ fontSize: 12, cursor: "help" }}
                    title="Ingreso Bruto − Costo Total (Resumen por Cultivo)"
                  />
                </small>
              </CardBody>
            </Card>
          </Col>        </Row>
      )}

      <Row className="mb-3">
        {/* Distribución por Cultivo (gráfico) */}
        <Col lg={4}>
          <Card className="h-100">
            <CardBody>
              <h6 className="card-title">Distribución por Cultivo</h6>
              {resumenCultivos.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={value => `${value.toLocaleString()} ha`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-4 text-muted">
                  Sin datos de siembras
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* =====================================================
            RESUMEN POR CULTIVO - MEJORADO CON FINANCIERO
            ===================================================== */}
        <Col lg={8}>
          <Card className="h-100">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title mb-0">
                  Resumen por Cultivo
                  {campanasIncluidas.length > 0 && (
                    <small
                      className="text-muted ms-2 fw-normal"
                      data-testid="rotulo-campanas"
                      title={TEXTO_SIEMBRAS_VIGENTES}
                    >
                      Siembras vigentes · campaña {campanasIncluidas.join(", ")}
                    </small>
                  )}
                </h6>
                {valoresAgro.length === 0 && resumenCultivos.length > 0 && (
                  <Badge color="warning" pill>
                    <i className="mdi mdi-alert me-1" />
                    Sin valores de referencia cargados
                  </Badge>
                )}
              </div>
              {resumenFinanciero.length > 0 ? (
                <div className="table-responsive">
                  <table
                    className="table table-sm table-hover mb-0"
                    data-testid="tabla-resumen-cultivo"
                  >
                    <thead>
                      <tr>
                        <th className="text-center">Cultivo</th>
                        <th className="text-center">Superficie</th>
                        <th className="text-center">Siembras</th>
                        <th className="text-center">Stock (qq)</th>
                        <th className="text-center">
                          Ingreso Bruto (USD)
                          <i
                            className="mdi mdi-information-outline ms-1 text-muted"
                            style={{ fontSize: 12, cursor: "help" }}
                            title="Producción estimada (tn) × precio de referencia (USD/tn) de la zona del establecimiento"
                          />
                        </th>
                        <th className="text-center">
                          Costo Total (USD)
                          <i
                            className="mdi mdi-information-outline ms-1 text-muted"
                            style={{ fontSize: 12, cursor: "help" }}
                            title="Superficie (ha) × costo total por ha de la zona del establecimiento (ya incluye comercialización, arrendamientos, administración y seguro granizo)"
                          />
                        </th>
                        <th className="text-center">
                          Resultado Neto (USD)
                          <i
                            className="mdi mdi-information-outline ms-1 text-muted"
                            style={{ fontSize: 12, cursor: "help" }}
                            title="Ingreso Bruto − Costo Total"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumenFinanciero.map((r, idx) => (
                        <tr key={idx}>
                          <td>
                            <span
                              className="me-2"
                              style={{
                                display: "inline-block",
                                width: 12,
                                height: 12,
                                backgroundColor: COLORS[idx % COLORS.length],
                                borderRadius: 2,
                              }}
                            />
                            {r.nombreCultivo}
                            {!r.tieneValoresRef && (
                              <i
                                className="mdi mdi-alert-circle text-warning ms-1"
                                title={[
                                  r.parcial
                                    ? TEXTO_TOTALES_PARCIALES
                                    : "Sin valores de referencia para este cultivo en la zona del establecimiento",
                                  r.sinZona > 0
                                    ? "Establecimiento inactivo: sin zona"
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(". ")}
                                style={{ fontSize: 12 }}
                              />
                            )}
                            {r.parcial && (
                              <small
                                className="d-block text-warning"
                                data-testid="aviso-totales-parciales"
                              >
                                {TEXTO_TOTALES_PARCIALES}
                              </small>
                            )}
                            {r.rendimientoRef > 0 && (
                              <small
                                className="d-block text-muted"
                                data-testid="rendimiento-ref"
                              >
                                {`Rend. ref.: ${formatearNumero(r.rendimientoRef)} qq/ha`}
                              </small>
                            )}
                          </td>
                          <td className="text-end">
                            {r.superficieHa > 0
                              ? `${formatearNumero(r.superficieHa)} ha`
                              : "-"}
                          </td>
                          <td className="text-center">
                            {r.cantidadSiembras > 0 ? r.cantidadSiembras : "-"}
                          </td>
                          <td className="text-end">
                            {r.stockQq > 0 ? formatearNumero(r.stockQq) : "-"}
                          </td>
                          <td className="text-end text-success">
                            {r.sinDatos
                              ? "s/d"
                              : r.ingresoBruto > 0
                                ? formatRedondeadoArriba(r.ingresoBruto)
                                : "-"}
                          </td>
                          <td className="text-end text-danger">
                            {r.sinDatos
                              ? "s/d"
                              : r.costoTotal > 0
                                ? formatRedondeadoArriba(r.costoTotal)
                                : "-"}
                          </td>
                          <td
                            className={`text-end fw-bold ${r.resultadoNeto >= 0 ? "text-success" : "text-danger"}`}
                          >
                            {r.sinDatos
                              ? "s/d"
                              : r.ingresoBruto > 0 || r.costoTotal > 0
                                ? formatRedondeadoArriba(r.resultadoNeto)
                                : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-light fw-bold">
                        <td>Total</td>
                        <td className="text-end">
                          {totales.superficie > 0
                            ? `${formatearNumero(totales.superficie)} ha`
                            : "-"}
                        </td>
                        <td className="text-center">
                          {totales.siembras > 0 ? totales.siembras : "-"}
                        </td>
                        <td className="text-end">
                          {totales.stockQq > 0
                            ? formatearNumero(totales.stockQq)
                            : "-"}
                        </td>
                        <td className="text-end text-success">
                          {formatRedondeadoArriba(totales.ingresoBruto)}
                        </td>
                        <td className="text-end text-danger">
                          {formatRedondeadoArriba(totales.costoTotal)}
                        </td>
                        <td
                          className={`text-end ${totales.resultadoNeto >= 0 ? "text-success" : "text-danger"}`}
                        >
                          {formatRedondeadoArriba(totales.resultadoNeto)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {Object.keys(overrides).length > 0 && (
                    <div className="text-end mt-1">
                      <small className="text-muted me-2">
                        <i className="mdi mdi-pencil me-1" />
                        Valores editados por el usuario
                      </small>
                      <Button
                        size="sm"
                        color="link"
                        className="p-0 text-muted"
                        onClick={() => setOverrides({})}
                      >
                        Restaurar originales
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  Sin datos de siembras ni stock registrado
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ===================================================== */}
      {/* ACTIVIDAD AGRÍCOLA - TABS                              */}
      {/* ===================================================== */}
      <style>
        {`
          .nav-tabs-agro .nav-link.active {
            color: #495057 !important;
            background-color: #fff !important;
            font-weight: 600 !important;
            border-color: #dee2e6 #dee2e6 #fff !important;
          }
          .nav-tabs-agro .nav-link {
            color: #6c757d !important;
            cursor: pointer;
          }
          .nav-tabs-agro .nav-link:hover:not(.active) {
            color: #495057 !important;
            border-color: #e9ecef #e9ecef #dee2e6 !important;
          }
        `}
      </style>

      {/* Tabs Actividad Agrícola */}
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Nav tabs className="nav-tabs-agro">
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTabAgricola === "siembras",
                  })}
                  onClick={() => setActiveTabAgricola("siembras")}
                  style={{ cursor: "pointer" }}
                >
                  Siembras
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTabAgricola === "resumen",
                  })}
                  onClick={() => setActiveTabAgricola("resumen")}
                  style={{ cursor: "pointer" }}
                >
                  Stock Actual
                </NavLink>
              </NavItem>
              {MOSTRAR_TAB_MOVIMIENTOS && (
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: activeTabAgricola === "movimientos",
                    })}
                    onClick={() => setActiveTabAgricola("movimientos")}
                    style={{ cursor: "pointer" }}
                  >
                    Movimientos
                  </NavLink>
                </NavItem>
              )}
            </Nav>
            {activeTabAgricola === "siembras" &&
              puedeModificarObjeto("/agro/establecimientos") && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => setModalNueva(true)}
                >
                  <i className="mdi mdi-plus me-1" />
                  Nueva Siembra
                </Button>
              )}
            {activeTabAgricola === "resumen" &&
              puedeModificarObjeto("/agro/establecimientos") && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => setModalStockAgricola(true)}
                >
                  <i className="mdi mdi-plus me-1" />
                  Registrar Stock
                </Button>
              )}
            {MOSTRAR_TAB_MOVIMIENTOS &&
              activeTabAgricola === "movimientos" &&
              puedeModificarObjeto("/agro/establecimientos") && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => setModalMovimientoAgricola(true)}
                >
                  <i className="mdi mdi-plus me-1" />
                  Nuevo Movimiento
                </Button>
              )}
          </div>

          {activeTabAgricola === "siembras" && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.establecimiento}
                    onChange={e =>
                      setFiltros({
                        ...filtros,
                        establecimiento: e.target.value,
                      })
                    }
                  >
                    <option value="">Todos los establecimientos</option>
                    {establecimientos.map(est => (
                      <option key={est.id} value={est.id}>
                        {est.nombre}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.campana}
                    onChange={e =>
                      setFiltros({ ...filtros, campana: e.target.value })
                    }
                  >
                    <option value="">Todas las campañas</option>
                    {campanas.map(c => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.codigo}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.cultivo}
                    onChange={e =>
                      setFiltros({ ...filtros, cultivo: e.target.value })
                    }
                  >
                    <option value="">Todos los cultivos</option>
                    {[...new Set(siembras.map(s => s.cultivo).filter(Boolean))]
                      .sort()
                      .map(c => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                  </Input>
                </Col>
              </Row>
              <DataTable
                columns={columns}
                data={siembrasFiltradas}
                pagination
                paginationPerPage={10}
                {...opcionesPaginacion(siembrasFiltradas.length)}
                highlightOnHover
                noDataComponent={
                  <div className="text-center py-4">
                    <i
                      className="mdi mdi-sprout text-muted"
                      style={{ fontSize: 48 }}
                    />
                    <p className="text-muted mt-2">
                      No hay siembras registradas
                    </p>
                  </div>
                }
              />
            </>
          )}

          {activeTabAgricola === "resumen" && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.establecimiento}
                    onChange={e =>
                      setFiltros({
                        ...filtros,
                        establecimiento: e.target.value,
                      })
                    }
                  >
                    <option value="">Todos los establecimientos</option>
                    {establecimientos.map(est => (
                      <option key={est.id} value={est.id}>
                        {est.nombre}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.campana}
                    onChange={e =>
                      setFiltros({ ...filtros, campana: e.target.value })
                    }
                  >
                    <option value="">Todas las campañas</option>
                    {campanas.map(c => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.codigo}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.cultivo}
                    onChange={e =>
                      setFiltros({ ...filtros, cultivo: e.target.value })
                    }
                  >
                    <option value="">Todos los cultivos</option>
                    {[
                      ...new Set(
                        stockAgricola.map(s => s.cultivo).filter(Boolean),
                      ),
                    ]
                      .sort()
                      .map(c => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                  </Input>
                </Col>
              </Row>
              <DataTable
                className="tabla-stock"
                columns={columnsStockAgricola}
                data={stockAgricolaFiltrado}
                pagination
                paginationPerPage={10}
                {...opcionesPaginacion(stockAgricolaFiltrado.length)}
                highlightOnHover
                noDataComponent={
                  <div className="text-center py-4">
                    <i
                      className="mdi mdi-silo text-muted"
                      style={{ fontSize: 48 }}
                    />
                    <p className="text-muted mt-2">
                      No hay stock agrícola registrado
                    </p>
                  </div>
                }
              />
            </>
          )}

          {MOSTRAR_TAB_MOVIMIENTOS && activeTabAgricola === "movimientos" && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.establecimiento}
                    onChange={e =>
                      setFiltros({
                        ...filtros,
                        establecimiento: e.target.value,
                      })
                    }
                  >
                    <option value="">Todos los establecimientos</option>
                    {establecimientos.map(est => (
                      <option key={est.id} value={est.id}>
                        {est.nombre}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.campana}
                    onChange={e =>
                      setFiltros({ ...filtros, campana: e.target.value })
                    }
                  >
                    <option value="">Todas las campañas</option>
                    {campanas.map(c => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.codigo}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={filtros.cultivo}
                    onChange={e =>
                      setFiltros({ ...filtros, cultivo: e.target.value })
                    }
                  >
                    <option value="">Todos los cultivos</option>
                    {[
                      ...new Set(
                        movimientosAgricola.map(m => m.cultivo).filter(Boolean),
                      ),
                    ]
                      .sort()
                      .map(c => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                  </Input>
                </Col>
              </Row>
              <DataTable
                className="tabla-movimientos"
                columns={columnsMovimientosAgricola}
                data={movimientosAgricolaFiltrados}
                pagination
                paginationPerPage={10}
                {...opcionesPaginacion(movimientosAgricolaFiltrados.length)}
                highlightOnHover
                fixedHeader
                noDataComponent={
                  <div className="text-center py-4">
                    <p className="text-muted">
                      No hay movimientos agrícolas registrados
                    </p>
                  </div>
                }
              />
            </>
          )}
        </CardBody>
      </Card>

      {/* Modals Stock Agrícola */}
      <Modal
        isOpen={modalStockAgricola}
        toggle={() => setModalStockAgricola(false)}
        size="xl"
      >
        <ModalHeader toggle={() => setModalStockAgricola(false)}>
          Registrar Stock Agrícola
        </ModalHeader>
        <ModalBody>
          <FormStockAgricola
            establecimientos={establecimientos}
            idEstablecimiento={filtros.establecimiento}
            onSuccess={() => {
              setModalStockAgricola(false)
              loadData()
            }}
            onCancel={() => setModalStockAgricola(false)}
          />
        </ModalBody>
      </Modal>

      {MOSTRAR_TAB_MOVIMIENTOS && (
        <Modal
          isOpen={modalMovimientoAgricola}
          toggle={() => setModalMovimientoAgricola(false)}
          size="xl"
        >
          <ModalHeader toggle={() => setModalMovimientoAgricola(false)}>
            Registrar Movimiento Agrícola
          </ModalHeader>
          <ModalBody>
            <FormMovimientoAgricola
              idEstablecimiento={filtros.establecimiento}
              establecimientos={establecimientos}
              onSuccess={() => {
                setModalMovimientoAgricola(false)
                loadMovimientosAgricola()
                loadData()
              }}
              onCancel={() => setModalMovimientoAgricola(false)}
            />
          </ModalBody>
        </Modal>
      )}

      {/* Modal Nueva Siembra */}
      <Modal isOpen={modalNueva} toggle={() => setModalNueva(false)} size="xl">
        <ModalHeader toggle={() => setModalNueva(false)}>
          Nueva Siembra
        </ModalHeader>
        <ModalBody>
          <FormSiembra
            idProductor={idProductor}
            establecimientos={establecimientos}
            onSuccess={() => {
              setModalNueva(false)
              loadData()
            }}
            onCancel={() => setModalNueva(false)}
          />
        </ModalBody>
      </Modal>

      {/* Modal Editar Siembra */}
      <Modal
        isOpen={modalEditarSiembra}
        toggle={() => {
          setModalEditarSiembra(false)
          setSiembraAEditar(null)
        }}
        size="xl"
      >
        <ModalHeader
          toggle={() => {
            setModalEditarSiembra(false)
            setSiembraAEditar(null)
          }}
        >
          Editar Siembra
        </ModalHeader>
        <ModalBody>
          <FormSiembra
            idProductor={idProductor}
            establecimientos={establecimientos}
            siembraAEditar={siembraAEditar}
            onSuccess={() => {
              setModalEditarSiembra(false)
              setSiembraAEditar(null)
              loadData()
            }}
            onCancel={() => {
              setModalEditarSiembra(false)
              setSiembraAEditar(null)
            }}
          />
        </ModalBody>
      </Modal>

      {/* Modal Confirmar Eliminar Siembra */}
      <Modal
        isOpen={!!siembraAEliminar}
        toggle={() => setSiembraAEliminar(null)}
        size="sm"
      >
        <ModalHeader toggle={() => setSiembraAEliminar(null)}>
          Confirmar eliminación
        </ModalHeader>
        <ModalBody>
          <p>
            ¿Eliminar la siembra de <strong>{siembraAEliminar?.cultivo}</strong>{" "}
            en <strong>{siembraAEliminar?.establecimiento}</strong>?
          </p>
          <div className="d-flex gap-2 justify-content-end">
            <Button color="secondary" onClick={() => setSiembraAEliminar(null)}>
              Cancelar
            </Button>
            <Button
              color="danger"
              onClick={async () => {
                try {
                  await deleteSiembra(siembraAEliminar.id)
                  setSuccess("Siembra eliminada correctamente")
                  // BDSJEA-208 hotfix: se actualiza solo el listado de
                  // siembras en memoria y se refresca el resumen que
                  // depende de él, en vez de llamar a loadData() (que
                  // recargaba las 7 fuentes con loading global y producía
                  // el refresco de pantalla completa). Tampoco depende del
                  // fix de backend BDSJEA-207 (filtro faltante en el
                  // repository): la siembra se quita del estado local sin
                  // esperar a que el GET la excluya.
                  setSiembras(prev =>
                    prev.filter(s => s.id !== siembraAEliminar.id),
                  )
                  setSiembraAEliminar(null)
                  refrescarResumenSiembras()
                } catch (e) {
                  setError("Error al eliminar la siembra")
                }
              }}
            >
              Eliminar
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Modal Editar Stock Agrícola */}
      <Modal
        isOpen={modalEditarStock}
        toggle={() => {
          setModalEditarStock(false)
          setStockAEditar(null)
        }}
        size="xl"
      >
        <ModalHeader
          toggle={() => {
            setModalEditarStock(false)
            setStockAEditar(null)
          }}
        >
          Editar Stock Agrícola
        </ModalHeader>
        <ModalBody>
          <FormStockAgricola
            establecimientos={establecimientos}
            stockAEditar={stockAEditar}
            onSuccess={() => {
              setModalEditarStock(false)
              setStockAEditar(null)
              loadData()
            }}
            onCancel={() => {
              setModalEditarStock(false)
              setStockAEditar(null)
            }}
          />
        </ModalBody>
      </Modal>

      {/* Modal Confirmar Eliminar Stock */}
      <Modal
        isOpen={!!stockAEliminar}
        toggle={() => setStockAEliminar(null)}
        size="sm"
      >
        <ModalHeader toggle={() => setStockAEliminar(null)}>
          Confirmar eliminación
        </ModalHeader>
        <ModalBody>
          <p>
            ¿Eliminar el stock de <strong>{stockAEliminar?.cultivo}</strong> en{" "}
            <strong>{stockAEliminar?.nombreEstablecimiento}</strong>?
          </p>
          <div className="d-flex gap-2 justify-content-end">
            <Button color="secondary" onClick={() => setStockAEliminar(null)}>
              Cancelar
            </Button>
            <Button
              color="danger"
              onClick={async () => {
                try {
                  await deleteStockAgricola(stockAEliminar.id)
                  setSuccess("Stock eliminado correctamente")
                  setStockAEliminar(null)
                  loadData()
                } catch (e) {
                  setError("Error al eliminar el stock")
                }
              }}
            >
              Eliminar
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default ActividadAgricola
